import React, { lazy, useMemo, useState } from "react"
import { Segmented } from "antd"

import type { LineageViewProps } from "./LineageView.component"
import { GraphSuspense } from "./GraphSuspense.component"
import { ScriptLog } from "./ScriptLog.component"
import { groupLogByCall } from "../utils/lineageCalls"

// The graph library and its layout load the first time a lineage graph is shown, not with the app
const LineageViewGraph = lazy(() => import("./LineageView.component"))

type Shown = "graph" | "scripts"

/**
 * The lineage graph, loaded on demand. Every view that shows one goes through here. A DAG with a
 * command log (schema 4) or a script log (schema 3) can also show the calls that were run.
 */
export const LazyLineageView: React.FC<LineageViewProps> = (props) => {
  const [shown, setShown] = useState<Shown>("graph")
  const { log, scripts = [], engineVersion } = props.dag
  // The calls the log holds: schema 4 groups its commands by call, schema 3 has one script per call
  const callCount = useMemo(() => (log ? groupLogByCall(log).length : scripts.length), [log, scripts])

  const graph = (
    <GraphSuspense>
      <LineageViewGraph {...props} />
    </GraphSuspense>
  )
  if (callCount === 0) return graph

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-slate-100 px-4 py-2">
        <Segmented<Shown>
          size="small"
          value={shown}
          onChange={setShown}
          options={[
            { label: "Graph", value: "graph" },
            { label: `Scripts (${callCount})`, value: "scripts" },
          ]}
        />
      </div>
      <div className="min-h-0 flex-1">
        {shown === "graph" ? graph : (
          <ScriptLog scripts={scripts} log={log} engineVersion={engineVersion} labels={props.scriptLabels} />
        )}
      </div>
    </div>
  )
}

export default LazyLineageView
